import MirrorBackButton from "@/components/MirrorBackButton";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import {
  ChevronLeft, CreditCard,
  Sliders, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { useAvatarSnapshot } from "@/hooks/useAvatarSnapshot";

// ── Types ───────────────────────────────────────────────────────────────────

interface Theme {
  id: string;
  label: string;
  gradient: string;
  border: string;
  accent: string;
  glow: string;
}

// ── Static data ──────────────────────────────────────────────────────────────

const THEMES: Theme[] = [
  {
    id: "cyan",
    label: "Dark Cyan",
    gradient: "linear-gradient(135deg, #0a0a0f 0%, #0d1117 50%, #1a1a2e 100%)",
    border: "rgba(0,229,255,0.30)",
    accent: "#00e5ff",
    glow: "rgba(0,229,255,0.12)",
  },
  {
    id: "purple",
    label: "Dark Purple",
    gradient: "linear-gradient(135deg, #0d0a1a 0%, #1a0d2e 50%, #1a1a2e 100%)",
    border: "rgba(139,92,246,0.40)",
    accent: "#a78bfa",
    glow: "rgba(139,92,246,0.15)",
  },
  {
    id: "gold",
    label: "Dark Gold",
    gradient: "linear-gradient(135deg, #0f0e00 0%, #1a1500 50%, #1a1200 100%)",
    border: "rgba(245,158,11,0.40)",
    accent: "#f59e0b",
    glow: "rgba(245,158,11,0.12)",
  },
];

interface StatItem {
  emoji: string;
  labelKey: string;
  value: string;
}

interface BadgeItem {
  emoji: string;
  labelKey: string;
}

// Badges are static v1 placeholders; earning logic is a separate feature.
const BADGES: BadgeItem[] = [
  { emoji: "💪", labelKey: "identity.badges.athlete"    },
  { emoji: "📊", labelKey: "identity.badges.selfAware"  },
  { emoji: "🎯", labelKey: "identity.badges.deepWorker" },
];

const CARD_STATS_INFO_KEYS = [
  "identity.stat1",
  "identity.stat2",
  "identity.stat3",
  "identity.stat4",
];

// ── Mini SVG Gauge ───────────────────────────────────────────────────────────

const GaugeMini = ({ score, color }: { score: number; color: string }) => {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  return (
    <svg width={60} height={60} viewBox="0 0 60 60">
      <circle cx={30} cy={30} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={4} />
      <circle
        cx={30} cy={30} r={r} fill="none" stroke={color}
        strokeWidth={4} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 30 30)"
      />
      <text
        x={30} y={35} textAnchor="middle" fontSize={13} fontWeight="bold"
        fill={color} fontFamily="system-ui, sans-serif"
      >
        {score}
      </text>
    </svg>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

interface UserProfileBrief {
  first_name: string | null;
  created_at: string | null;
  email: string | null;
}

const IdentityCard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTheme, setActiveTheme] = useState<string>("cyan");
  const [userProfile, setUserProfile] = useState<UserProfileBrief | null>(null);
  const [mirrorChatCount, setMirrorChatCount] = useState<number | null>(null);

  const { snapshotUrl, loading: snapshotLoading, hasAvatar } = useAvatarSnapshot();

  // SPINNER TIMEOUT (12/06 rule: no spinner without one, anywhere). The hook
  // now has its own 10s resolve ceiling, but this local 6s cap guarantees the
  // avatar circle degrades to the silhouette/initial fallback even if some
  // future hook regression wedges `loading` true again.
  const [spinnerTimedOut, setSpinnerTimedOut] = useState(false);
  useEffect(() => {
    if (!snapshotLoading) return;
    const tm = window.setTimeout(() => setSpinnerTimedOut(true), 6000);
    return () => window.clearTimeout(tm);
  }, [snapshotLoading]);

  const theme = THEMES.find((t) => t.id === activeTheme) ?? THEMES[0];

  // Fetch user profile + mirror chat count on mount.
  // NOTE: production user_profiles has `first_name` but not `full_name` or
  // `username`. Display name falls back to email prefix; handle is always the
  // email prefix. Year derived from created_at.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;

        const { data: profile, error: profileErr } = await (supabase as any)
          .from("user_profiles")
          .select("first_name, created_at")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileErr) {
          console.error("[IdentityCard] profile fetch failed:", profileErr);
        }

        if (!cancelled) {
          setUserProfile({
            first_name: (profile?.first_name as string) ?? null,
            created_at: (profile?.created_at as string) ?? user.created_at ?? null,
            email: user.email ?? null,
          });
        }

        const { count, error: countErr } = await supabase
          .from("mirror_messages")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("role", "user");

        if (countErr) {
          console.error("[IdentityCard] mirror chats count failed:", countErr);
        }
        if (!cancelled) setMirrorChatCount(count ?? 0);
      } catch (err) {
        console.error("[IdentityCard] load error:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Derived display values ──
  const emailPrefix = userProfile?.email?.split("@")[0] ?? null;
  const displayName = userProfile?.first_name?.trim() || emailPrefix || "—";
  const handle = emailPrefix ?? "—";

  const initials = (() => {
    const name = userProfile?.first_name?.trim();
    if (name) {
      const parts = name.split(/\s+/);
      const first = parts[0]?.charAt(0) ?? "";
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
      const combined = (first + last).toUpperCase();
      if (combined) return combined;
    }
    if (emailPrefix) return emailPrefix.slice(0, 2).toUpperCase();
    return "—";
  })();

  const memberSinceDate = (() => {
    if (!userProfile?.created_at) return "";
    const d = new Date(userProfile.created_at);
    if (Number.isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${mm}/${d.getFullYear()}`;
  })();

  const STATS: StatItem[] = [
    { emoji: "🔥", labelKey: "identity.stats.streak",   value: "7d"   },
    { emoji: "✅", labelKey: "identity.stats.habits",   value: "89%"  },
    { emoji: "💪", labelKey: "identity.stats.workouts", value: "4"    },
    { emoji: "😊", labelKey: "identity.stats.mood",     value: "Good" },
    { emoji: "🎯", labelKey: "identity.stats.tasks",    value: "90%"  },
    { emoji: "💬", labelKey: "identity.stats.mirror",   value: mirrorChatCount !== null ? String(mirrorChatCount) : "—" },
  ];

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* Back */}
      <MirrorBackButton className="mb-4" />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <p
            className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}
          >
            {t("identity.subtitle")}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("identity.title")}
          </h1>
        </div>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
        >
          <CreditCard size={15} style={{ color: "hsl(187 72% 50%)" }} />
        </div>
      </motion.div>

      {/* ── THE CARD ── */}
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto w-full mb-5"
        style={{ maxWidth: 320 }}
      >
        <div
          className="w-full flex flex-col p-6"
          style={{
            background: theme.gradient,
            border: `1px solid ${theme.border}`,
            borderRadius: 24,
            boxShadow: `0 0 40px ${theme.glow}, 0 20px 60px rgba(0,0,0,0.5)`,
            aspectRatio: "9 / 16",
          }}
        >
          {/* Top row */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-1.5">
              <div
                className="w-5 h-5 rounded-sm flex items-center justify-center"
                style={{ background: `${theme.accent}22`, border: `1px solid ${theme.accent}45` }}
              >
                <span style={{ color: theme.accent, fontSize: 8, fontWeight: "800", lineHeight: 1 }}>V</span>
              </div>
              <span style={{ color: theme.accent, fontSize: 9, fontWeight: "700", letterSpacing: "0.14em", fontFamily: "system-ui" }}>
                VYV MIRROR
              </span>
            </div>
            <span
              className="px-2 py-0.5 rounded-full"
              style={{
                fontSize: 8, fontWeight: "700", letterSpacing: "0.15em",
                color: theme.accent,
                border: `1px solid ${theme.accent}50`,
                background: `${theme.accent}10`,
                fontFamily: "system-ui",
              }}
            >
              IDENTITY CARD
            </span>
          </div>

          {/* Avatar */}
          <div className="flex flex-col items-center mb-4">
            {hasAvatar && snapshotUrl ? (
              <img
                src={snapshotUrl}
                alt={displayName}
                className="w-20 h-20 rounded-full object-cover mb-2.5"
                style={{ border: `2px solid ${theme.accent}45` }}
              />
            ) : snapshotLoading && !spinnerTimedOut ? (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-2.5"
                style={{
                  background: `linear-gradient(135deg, ${theme.accent}35, hsl(265 60% 40% / 0.45))`,
                  border: `2px solid ${theme.accent}45`,
                }}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full animate-pulse"
                  style={{
                    background: theme.accent,
                    boxShadow: `0 0 12px ${theme.accent}, 0 0 24px ${theme.accent}80`,
                  }}
                />
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-2.5"
                style={{
                  background: `linear-gradient(135deg, ${theme.accent}35, hsl(265 60% 40% / 0.45))`,
                  border: `2px solid ${theme.accent}45`,
                }}
              >
                <span style={{ color: theme.accent, fontSize: 22, fontWeight: "bold", fontFamily: "system-ui" }}>
                  {initials}
                </span>
              </div>
            )}
            <p style={{ color: "#ffffff", fontSize: 16, fontWeight: "700", fontFamily: "system-ui", marginBottom: 3 }}>
              {displayName}
            </p>
            <p style={{ color: "rgba(255,255,255,0.42)", fontSize: 10, fontFamily: "system-ui" }}>
              {memberSinceDate
                ? `${t("identity.memberSince")} ${memberSinceDate}`
                : t("identity.memberSince")}
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-px mb-4" style={{ background: `${theme.accent}22` }} />

          {/* Gauge */}
          <div className="flex flex-col items-center mb-4">
            <GaugeMini score={72} color={theme.accent} />
            <p style={{ color: theme.accent, fontSize: 8, fontWeight: "700", letterSpacing: "0.18em", marginTop: 3, fontFamily: "system-ui" }}>
              {t("identity.mirrorScore")}
            </p>
          </div>

          {/* Stats grid 2x3 */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {STATS.map((s) => (
              <div
                key={s.labelKey}
                className="rounded-lg p-1.5 text-center"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <p style={{ fontSize: 12 }}>{s.emoji}</p>
                <p style={{ color: "#ffffff", fontSize: 10, fontWeight: "700", fontFamily: "system-ui", lineHeight: 1.3 }}>{s.value}</p>
                <p style={{ color: "rgba(255,255,255,0.38)", fontSize: 8, fontFamily: "system-ui" }}>{t(s.labelKey)}</p>
              </div>
            ))}
          </div>

          {/* Badges */}
          <div className="flex gap-2 justify-center mb-4">
            {BADGES.map((b) => (
              <div
                key={b.labelKey}
                className="flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-lg"
                style={{ background: `${theme.accent}14`, border: `1px solid ${theme.accent}28` }}
              >
                <span style={{ fontSize: 14 }}>{b.emoji}</span>
                <span style={{ color: theme.accent, fontSize: 8, fontWeight: "700", fontFamily: "system-ui", letterSpacing: "0.04em" }}>
                  {t(b.labelKey)}
                </span>
              </div>
            ))}
          </div>

          {/* Quote + footer */}
          <div className="flex-1 flex flex-col justify-end">
            <p style={{ color: "rgba(255,255,255,0.48)", fontSize: 10, fontStyle: "italic", fontFamily: "system-ui", lineHeight: 1.55, marginBottom: 10 }}>
              "{t("identity.quote")}"
            </p>
            <p style={{ color: "rgba(255,255,255,0.22)", fontSize: 9, fontFamily: "system-ui" }}>
              vyv.app · @{handle}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Sharing is not implemented: never claim that a private link was copied. */}
      <p className="mb-6 rounded-2xl border border-border/40 bg-card px-4 py-3 text-sm leading-relaxed text-muted-foreground">
        {t("ux.identity.sharingPending")}
      </p>

      {/* ── Customize ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.28, duration: 0.5 }}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Sliders size={13} style={{ color: "hsl(var(--primary))" }} />
          <p className="text-[9px] font-display tracking-[0.18em] uppercase"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t("identity.customizeCard")}
          </p>
        </div>

        <p className="text-[10px] font-display font-semibold text-foreground mb-2.5">{t("identity.theme")}</p>
        <div className="flex gap-2 mb-5">
          {THEMES.map((th) => (
            <button
              key={th.id}
              onClick={() => setActiveTheme(th.id)}
              className="flex-1 py-2.5 rounded-xl flex flex-col items-center gap-1.5 transition-all active:scale-[0.96]"
              style={{
                background: activeTheme === th.id ? `${th.accent}18` : "hsl(187 72% 50% / 0.04)",
                border: `1.5px solid ${activeTheme === th.id ? th.accent : "hsl(187 72% 50% / 0.18)"}`,
              }}
            >
              <div
                className="w-5 h-5 rounded-full"
                style={{ background: th.gradient, border: `1.5px solid ${th.border}` }}
              />
              <span style={{
                fontSize: 9, fontWeight: "700", fontFamily: "system-ui",
                color: activeTheme === th.id ? th.accent : "hsl(218 14% 52%)",
                letterSpacing: "0.04em",
              }}>
                {th.label.split(" ")[1]}
              </span>
            </button>
          ))}
        </div>

        <p className="text-[10px] font-display font-semibold text-foreground mb-2.5">{t("identity.cardStyle")}</p>
        <div className="flex gap-2">
          <button
            className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold tracking-wide"
            style={{ background: "#00e5ff18", border: "1.5px solid #00e5ff", color: "hsl(var(--primary))" }}
          >
            {t("identity.vertical")}
          </button>
          <button
            onClick={() => toast(t("identity.horizontalComingSoon"))}
            className="flex-1 py-2.5 rounded-xl font-display text-[11px] font-semibold tracking-wide"
            style={{ background: "hsl(187 72% 50% / 0.04)", border: "1px solid hsl(187 72% 50% / 0.18)", color: "hsl(218 14% 52%)" }}
          >
            {t("identity.horizontal")}
          </button>
        </div>
      </motion.div>

      {/* ── Card Stats ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.34, duration: 0.5 }}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("identity.cardStatsLabel")}</p>
        <p className="text-[13px] font-display font-semibold text-foreground mb-3">{t("identity.whatCardShows")}</p>
        <div className="flex flex-col gap-2">
          {CARD_STATS_INFO_KEYS.map((key, i) => (
            <p key={i} className="text-[11px] font-body leading-relaxed" style={{ color: "hsl(218 14% 55%)" }}>
              · {t(key)}
            </p>
          ))}
        </div>
      </motion.div>

      {/* ── Mirror card ── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.40, duration: 0.5 }}
        className="rounded-2xl p-4 mb-5 relative overflow-hidden"
        style={{ background: "hsl(187 72% 50% / 0.05)", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1.5"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>Mirror</p>
        <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
          {t("identity.mirrorDescription")}
        </p>
        <button
          onClick={() => navigate("/mirror")}
          className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
          style={{ color: "hsl(187 72% 55%)" }}
        >
          <span>{t("identity.updateMirror")}</span>
          <ArrowRight size={11} />
        </button>
      </motion.div>

    </MobileLayout>
  );
};

export default IdentityCard;
